import React, { useCallback, useEffect, useState } from 'react';
import { Facebook, CheckCircle2, AlertTriangle, Loader2, ExternalLink, Trash2, ShieldCheck } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { toast } from 'sonner';
import { adminGet, adminPost, adminDelete, reportError, formatDate } from './adminApi';

/**
 * Settings card: Facebook Page connection used to auto-share blog articles.
 * The page token is write-only (never returned by the API, only its last 4 characters).
 */
export function AdminFacebookCard({ adminKey }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [form, setForm] = useState({ page_id: '', page_token: '', enabled: true, graph_version: 'v26.0', hashtags: '' });
  const [validation, setValidation] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/facebook/settings', adminKey);
      setSettings(res.data);
      setForm((f) => ({ ...f, page_id: res.data.page_id || '', enabled: res.data.enabled, graph_version: res.data.graph_version, hashtags: res.data.hashtags || '' }));
    } catch (err) {
      reportError('paramètres Facebook', err, () => toast.error('Impossible de charger les paramètres Facebook'));
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminPost('/api/admin/facebook/settings', adminKey, {}, { ...form, page_token: form.page_token.trim() || null });
      setSettings(res.data.settings);
      setForm((f) => ({ ...f, page_token: '' }));
      setValidation(null);
      toast.success('Paramètres Facebook enregistrés');
    } catch (err) {
      reportError('enregistrement Facebook', err, (m) => toast.error(m || 'Enregistrement impossible'));
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    setValidating(true); setValidation(null);
    try {
      const res = await adminPost('/api/admin/facebook/validate', adminKey);
      setValidation({ ok: true, ...res.data });
      toast.success(`Connexion réussie avec la page « ${res.data.page_name} »`);
      load();
    } catch (err) {
      const msg = reportError('validation Facebook', err);
      setValidation({ ok: false, error: msg || 'Vérification impossible' });
      toast.error('La connexion Facebook a échoué');
    } finally {
      setValidating(false);
    }
  };

  const clearToken = async () => {
    try {
      const res = await adminDelete('/api/admin/facebook/token', adminKey);
      setSettings(res.data.settings); setValidation(null);
      toast.success('Jeton supprimé');
    } catch (err) {
      reportError('suppression jeton Facebook', err, (m) => toast.error(m || 'Suppression impossible'));
    }
  };

  const statusBadge = () => {
    if (!settings?.configured) return <Badge variant="secondary" data-testid="fb-status-badge">Non configuré</Badge>;
    if (!settings.enabled) return <Badge variant="outline" data-testid="fb-status-badge">Désactivé</Badge>;
    if (settings.validated_at) return <Badge className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]" data-testid="fb-status-badge"><CheckCircle2 className="h-3 w-3 mr-1" /> Connecté</Badge>;
    return <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-400" data-testid="fb-status-badge">À vérifier</Badge>;
  };

  return (
    <Card className="p-5" data-testid="admin-facebook-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Facebook className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
          <div>
            <h3 className="font-['Space_Grotesk'] font-semibold">Page Facebook — partage automatique du blog</h3>
            <p className="text-xs text-muted-foreground">Chaque article publié est partagé sur la page avec une accroche rédigée par l'IA et un lien vers l'article.</p>
          </div>
        </div>
        {!loading && statusBadge()}
      </div>

      {loading ? (
        <div className="space-y-2 mt-4"><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-full" /></div>
      ) : (
        <div className="space-y-4 mt-4">
          {settings?.configured && (
            <div className="rounded-lg border border-border bg-[hsl(var(--secondary))]/50 p-3 text-xs space-y-1" data-testid="fb-current-state">
              <p>Page : <strong className="text-foreground">{settings.page_name || settings.page_id}</strong>{settings.page_name && <span className="text-muted-foreground"> (ID {settings.page_id})</span>}</p>
              <p>Jeton : <span className="font-mono">••••{settings.token_last4}</span>{settings.validated_at ? ` · vérifié le ${formatDate(settings.validated_at, true)}` : ' · non vérifié'}</p>
              {settings.last_error && <p className="text-destructive flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Dernière erreur : {settings.last_error}</p>}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="fb-page-id">Identifiant de la page (Page ID)</Label>
              <Input id="fb-page-id" className="h-9" value={form.page_id} onChange={(e) => setForm((f) => ({ ...f, page_id: e.target.value }))} placeholder="Ex : 123456789012345" inputMode="numeric" data-testid="fb-page-id-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fb-token">Jeton d'accès de page (longue durée)</Label>
              <Input id="fb-token" type="password" autoComplete="off" className="h-9" value={form.page_token} onChange={(e) => setForm((f) => ({ ...f, page_token: e.target.value }))} placeholder={settings?.has_token ? `Jeton enregistré (••••${settings.token_last4}) — laisser vide pour conserver` : 'EAAB…'} data-testid="fb-token-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="fb-hashtags">Hashtags par défaut</Label>
              <Input id="fb-hashtags" className="h-9" value={form.hashtags} onChange={(e) => setForm((f) => ({ ...f, hashtags: e.target.value }))} placeholder="#Ptitservices #ServicesALaPersonne #Artois" data-testid="fb-hashtags-input" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="fb-enabled-switch" />
              Partage automatique activé
            </label>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={save} disabled={saving} data-testid="fb-save-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Enregistrer</Button>
              <Button size="sm" variant="outline" onClick={validate} disabled={validating || !settings?.configured} data-testid="fb-validate-btn">{validating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />} Vérifier la connexion</Button>
              {settings?.has_token && <Button size="sm" variant="ghost" onClick={clearToken} className="text-destructive hover:text-destructive" data-testid="fb-clear-token-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Supprimer le jeton</Button>}
            </div>
          </div>

          {validation && (
            <div className={`rounded-lg border p-3 text-xs ${validation.ok ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--brand-mint))]/40' : 'border-destructive/40 bg-destructive/5'}`} data-testid="fb-validation-result">
              {validation.ok ? (
                <div className="space-y-1">
                  <p className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> Page « {validation.page_name} » connectée{validation.page_link && <a href={validation.page_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline">voir la page <ExternalLink className="h-3 w-3" /></a>}</p>
                  {validation.warning && <p className="text-amber-700 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {validation.warning}</p>}
                </div>
              ) : (
                <p className="flex items-start gap-1.5 text-destructive"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {validation.error}</p>
              )}
            </div>
          )}

          <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="link" size="sm" className="px-0 h-auto text-xs" data-testid="fb-help-toggle">{helpOpen ? 'Masquer' : 'Comment obtenir'} l'identifiant et le jeton de page ?</Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ol className="list-decimal pl-5 space-y-1.5 text-xs text-muted-foreground mt-2">
                <li>Créez une application sur <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">developers.facebook.com</a> (cas d'usage « Gérer tout sur votre page »).</li>
                <li>Dans l'Explorateur de l'API Graph, générez un jeton utilisateur avec les permissions <code className="font-mono">pages_show_list</code>, <code className="font-mono">pages_manage_posts</code> et <code className="font-mono">pages_read_engagement</code>.</li>
                <li>Prolongez-le en jeton longue durée (outil « Débogueur de jeton d'accès » → « Prolonger le jeton d'accès »).</li>
                <li>Appelez <code className="font-mono">/me/accounts</code> : la réponse contient l'<strong>id</strong> de la page et son <strong>access_token</strong> (jeton de page, sans expiration). Collez ces deux valeurs ci-dessus.</li>
                <li>Enregistrez puis cliquez sur « Vérifier la connexion ». Le jeton est stocké chiffré et n'est jamais renvoyé au navigateur.</li>
              </ol>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </Card>
  );
}
